import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, normalizeProps as _normalizeProps, guardReactiveProps as _guardReactiveProps, createVNode as _createVNode, createElementVNode as _createElementVNode, withCtx as _withCtx, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "grid grid-cols max-w-[1144px] max-w-176 w-full items-start self-stretch" }
const _hoisted_2 = { class: "mt-4" }
const _hoisted_3 = { class: "md:w-176 mt-2" }
const _hoisted_4 = { class: "mt-3" }
const _hoisted_5 = { class: "mt-10" }
const _hoisted_6 = { class: "mt-10" }
const _hoisted_7 = { class: "!mt-10 mb-10 md:!mt-12 md:!mb-12" }

import { onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { ISitecoreFormGenericInput } from '@/interfaces';
import FormStepInputs from '@/components/FormStepInputs/FormStepInputs.vue';
import { getFormInput } from '@/utils/form-input-helper';
import { useAccountStore } from '@/store';
import { PageAnalyticsBuilder } from '@/utils/page-analytics-builder';
import LinkPanel from '@/components/LinkPanel/LinkPanel.vue';
import { replaceBookmark } from '@/utils/bookmark.replace';
import { formatDate } from 'date-fns';
import { toProperCase } from '@/utils/text-transforms';
import { ToastAlertType } from '@/enums/toast-alert-enum';
import { useToastAlertStore } from '@/store/toast-alert/toast-alert.state';
import SectionPanel from '@/components/SectionPanel/SectionPanel.vue';
import PanelTitle from '@/components/PanelTitle/PanelTitle.vue';
import { STANDARD_DATE_FORMAT } from '@/constants/formats';
import Divider from '@/components/Divider/Divider.vue';

const copyMemberNumberContentElement = 'copyMemberNumber';

enum PersonalDetailsBookmarks {
  memberFullName = 'memberFullName',
  memberDateOfBirth = 'memberDateOfBirth',
  memberNumber = 'memberNumber',
}

enum ContactInformationBookmarks {
  addressTypesText = 'addressTypesText',
}


export default /*@__PURE__*/_defineComponent({
  __name: 'Profile',
  props: {
    content: {},
    id: {},
    name: {}
  },
  setup(__props: any) {

const props = __props;

const toastAlertStore = useToastAlertStore();
const accountStore = useAccountStore();
const dictionary = useI18n().getLocaleMessage('en') as Record<string, string>;

const contactAddressInformation = ref(getFormInput(props.content, 'contactAddressInformation'));
const accountDetails = ref(getFormInput(props.content, 'accountDetails'));
const accountPreferences = ref(getFormInput(props.content, 'accountPreferences'));

onMounted(() => {
  const copyElement = document.getElementById(copyMemberNumberContentElement);
  if (copyElement) {
    copyElement.replaceWith(copyElement.cloneNode(true));

    const freshCopyElement = document.getElementById(copyMemberNumberContentElement);

    if (freshCopyElement) {
      freshCopyElement.addEventListener('click', () => {
        navigator.clipboard
          .writeText(accountStore.identityDetails?.membershipCardNumber?.replaceAll(' ', '') ?? '')
          .then(() => {
            toastAlertStore.addToastAlert({
              title: 'Member number copied',
              description: '',
              type: ToastAlertType.SUCCESS,
              duration: 6000,
            });
          });
      });
    }
  }

  pushAnalytics();
});

function getDisplayName() {
  return `${toProperCase(accountStore.identityDetails?.title)} ${toProperCase(accountStore.identityDetails?.firstName)} ${toProperCase(accountStore.identityDetails?.lastName)}`;
}

function fillBookmarksPersonalDetailsContent(content: string) {
  if (content) {
    content = replaceBookmark(
      PersonalDetailsBookmarks.memberFullName,
      content,
      `${toProperCase(accountStore.identityDetails?.title)} ${toProperCase(accountStore.identityDetails?.firstName)} ${toProperCase(accountStore.identityDetails?.lastName)}`
    );

    content = replaceBookmark(
      PersonalDetailsBookmarks.memberDateOfBirth,
      content,
      formatDate(new Date(accountStore.identityDetails?.birthDate ?? ''), STANDARD_DATE_FORMAT)
    );

    content = replaceBookmark(
      PersonalDetailsBookmarks.memberNumber,
      content,
      accountStore.identityDetails?.membershipCardNumber ?? ''
    );
  }

  return content;
}

function fillContactInformationBookmarks(fields: any[]) {
  fields.forEach((field) => {
    if (field.subTitle?.value) {
      const textElements = ['Residential', 'postal', 'insured'];
      let addressesText = '';

      if ((accountStore.identityDetails?.portfolio?.insurancePolicies?.length ?? 0) > 0) {
        addressesText = `${textElements[0]}, ${textElements[1]} and ${textElements[2]} addresses`;
      } else {
        addressesText = `${textElements[0]} and ${textElements[1]} addresses`;
      }

      field.subTitle.value = replaceBookmark(
        ContactInformationBookmarks.addressTypesText,
        field.subTitle?.value,
        addressesText
      );
    }
  });
  return fields;
}

const pushAnalytics = async () => {
  const pageAnalytics = new PageAnalyticsBuilder();
  pageAnalytics.populateProducts(dictionary);
  pageAnalytics.pushAnalytics();
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(FormStepInputs, {
      step: {
        stepName: _ctx.name,
        formInputs: _ctx.content,
      }
    }, {
      personalDetailsTitle: _withCtx(() => [
        _createElementVNode("div", _hoisted_2, [
          _createVNode(PanelTitle, _normalizeProps(_guardReactiveProps(_unref(getFormInput)(props.content, 'personalDetailsTitle'))), null, 16)
        ])
      ]),
      personalDetails: _withCtx(() => [
        _createElementVNode("div", _hoisted_3, [
          _createVNode(SectionPanel, {
            ssGreyBg: { value: true },
            title: { value: getDisplayName() },
            description: {
              value: fillBookmarksPersonalDetailsContent(
                _unref(getFormInput)(props.content, 'personalDetailsContent')?.label?.value
              ),
            }
          }, null, 8, ["title", "description"])
        ])
      ]),
      contactAddressInformation: _withCtx(() => [
        _createElementVNode("div", _hoisted_4, [
          _createVNode(LinkPanel, {
            bindings: contactAddressInformation.value.formInputId?.value,
            fields: contactAddressInformation.value,
            heading: contactAddressInformation.value.heading,
            headingDescription: contactAddressInformation.value.headingDescription,
            links: fillContactInformationBookmarks(contactAddressInformation.value.fields),
            subheading: contactAddressInformation.value.subheading,
            subheadingDescription: contactAddressInformation.value.subheadingDescription,
            isGreyBackground: contactAddressInformation.value.isGreyBackground,
            isArrowLinks: contactAddressInformation.value.isArrowLinks,
            iconSize: contactAddressInformation.value.iconSize,
            globalIcon: contactAddressInformation.value.globalIcon,
            globalButtonLabel: contactAddressInformation.value.globalButtonLabel,
            handleLinkClickEvent: false
          }, null, 8, ["bindings", "fields", "heading", "headingDescription", "links", "subheading", "subheadingDescription", "isGreyBackground", "isArrowLinks", "iconSize", "globalIcon", "globalButtonLabel"])
        ])
      ]),
      accountDetails: _withCtx(() => [
        _createElementVNode("div", _hoisted_5, [
          _createVNode(LinkPanel, {
            bindings: accountDetails.value.formInputId?.value,
            fields: accountDetails.value,
            heading: accountDetails.value.heading,
            headingDescription: accountDetails.value.headingDescription,
            links: accountDetails.value.fields,
            subheading: accountDetails.value.subheading,
            subheadingDescription: accountDetails.value.subheadingDescription,
            isGreyBackground: accountDetails.value.isGreyBackground,
            isArrowLinks: accountDetails.value.isArrowLinks,
            iconSize: accountDetails.value.iconSize,
            globalIcon: accountDetails.value.globalIcon,
            globalButtonLabel: accountDetails.value.globalButtonLabel,
            handleLinkClickEvent: false
          }, null, 8, ["bindings", "fields", "heading", "headingDescription", "links", "subheading", "subheadingDescription", "isGreyBackground", "isArrowLinks", "iconSize", "globalIcon", "globalButtonLabel"])
        ])
      ]),
      accountPreferences: _withCtx(() => [
        _createElementVNode("div", _hoisted_6, [
          _createVNode(LinkPanel, {
            bindings: accountPreferences.value.formInputId?.value,
            fields: accountPreferences.value,
            heading: accountPreferences.value.heading,
            headingDescription: accountPreferences.value.headingDescription,
            links: accountPreferences.value.fields,
            subheading: accountPreferences.value.subheading,
            subheadingDescription: accountPreferences.value.subheadingDescription,
            isGreyBackground: accountPreferences.value.isGreyBackground,
            isArrowLinks: accountPreferences.value.isArrowLinks,
            iconSize: accountPreferences.value.iconSize,
            globalIcon: accountPreferences.value.globalIcon,
            globalButtonLabel: accountPreferences.value.globalButtonLabel,
            handleLinkClickEvent: false
          }, null, 8, ["bindings", "fields", "heading", "headingDescription", "links", "subheading", "subheadingDescription", "isGreyBackground", "isArrowLinks", "iconSize", "globalIcon", "globalButtonLabel"])
        ]),
        _createElementVNode("div", _hoisted_7, [
          _createVNode(Divider)
        ])
      ]),
      _: 1
    }, 8, ["step"])
  ]))
}
}

})